import React, { useEffect, useState, useMemo } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import Box from "@mui/material/Box";
import styles from "./Styles/ElectionDashboard.module.css";

/* ────────────────────── Custom fetch hook (with auth + dynamic creatorId) ────────────────────── */
const useApi = (baseUrl, creatorId) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Memoize final URL to prevent unnecessary re-renders
    const finalUrl = useMemo(() => {
        const authJson = localStorage.getItem("authData");
        const token = authJson ? JSON.parse(authJson).token : null;
        const idFromStorage = authJson ? JSON.parse(authJson).user?.id : null;
        const finalCreatorId = creatorId ?? idFromStorage;

        let url = baseUrl;
        if (finalCreatorId) {
            const hasQuery = url.includes("?");
            url = hasQuery ? `${url}&creatorId=${finalCreatorId}` : `${url}?creatorId=${finalCreatorId}`;
        }
        return url;
    }, [baseUrl, creatorId]);

    useEffect(() => {
        let mounted = true;

        const authJson = localStorage.getItem("authData");
        const token = authJson ? JSON.parse(authJson).token : null;

        fetch(finalUrl, {
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        })
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((json) => {
                if (mounted) setData(json);
            })
            .catch(() => {
                // Silent fail
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [finalUrl]); // Only depend on memoized URL

    return { data, loading };
};

/* ────────────────────── Dashboard Component ────────────────────── */
const ElectionDashboard = () => {
    // Memoize creatorId to avoid recreation on every render
    const creatorId = useMemo(() => {
        const authData = JSON.parse(localStorage.getItem("authData") || "{}");
        return authData?.user?.id || "68f6130be25c7a844362ee89";
    }, []);

    // API Calls – now stable URLs
    const promptRes = useApi(
        "https://prod.toxboxai.com/api/prompt-template/get-templates-by-creator",
        creatorId
    );

    const agentRes = useApi(
        `https://prod.toxboxai.com/api/election-agent/by-creator/${creatorId}`,
        creatorId
    );

    const campaignRes = useApi(
        `https://prod.toxboxai.com/api/election-campaign/by-creator/${creatorId}`,
        creatorId
    );

    const balanceRes = useApi(
        `https://prod.toxboxai.com/api/recharge/balance`,
        null // No creatorId needed
    );

    // Compute stats
    const totalCampaigns = campaignRes.data?.data?.length || 0;
    const totalVoters =
        campaignRes.data?.data?.reduce(
            (s, c) => s + (c.ConstituencyTotalVoters || 0),
            0
        ) || 0;

    /* Prompt → Bar Chart */
    const promptMap = (promptRes.data?.templates || []).reduce((acc, p) => {
        const name =
            p.type === "promise"
                ? "Promise"
                : p.type === "leader"
                    ? "Leader"
                    : p.type === "custom"
                        ? "Party"
                        : p.type === "influencer"
                            ? "Influencer"
                            : "Query";
        acc[name] = (acc[name] || 0) + 1;
        return acc;
    }, {});
    const barData = Object.entries(promptMap).map(([l, v]) => ({ label: l, value: v }));
    const barLabels = barData.map((d) => d.label);
    const barValues = barData.map((d) => d.value);

    /* Pie & Line mock data */
    const pieData = [
        { id: 0, value: 60, label: "Completed", color: "#00C49F" },
        { id: 1, value: 20, label: "Missed", color: "#FF4444" },
        { id: 2, value: 20, label: "Not Called", color: "#FFBB28" },
    ];
    const durationData = [
        { month: "Jan", Oncall: 85, Idle: 15 },
        { month: "Feb", Oncall: 78, Idle: 22 },
        { month: "Mar", Oncall: 82, Idle: 18 },
        { month: "Apr", Oncall: 88, Idle: 12 },
        { month: "May", Oncall: 75, Idle: 25 },
        { month: "Jun", Oncall: 80, Idle: 20 },
        { month: "Jul", Oncall: 83, Idle: 17 },
        { month: "Aug", Oncall: 90, Idle: 10 },
        { month: "Sep", Oncall: 87, Idle: 13 },
        { month: "Oct", Oncall: 85, Idle: 15 },
        { month: "Nov", Oncall: 88, Idle: 12 },
        { month: "Dec", Oncall: 86, Idle: 14 },
    ];
    const months = durationData.map((d) => d.month);
    const oncallVals = durationData.map((d) => d.Oncall);
    const idleVals = durationData.map((d) => d.Idle);

    // Loading state
    if (promptRes.loading || agentRes.loading || campaignRes.loading || balanceRes.loading) {
        return <div className={styles.loader}>Loading…</div>;
    }

    return (
        <div className={styles.dashboard}>
            {/* Main */}
            <main className={styles.main}>
                <header className={styles.header}>
                    <h1 className={styles.title}>Election Campaign Dashboard</h1>
                    <p className={styles.subtitle}>Overview of your AI voice platform Calling Status</p>
                </header>

                {/* Stats */}
                <section className={styles.statsGrid}>
                    {[
                        { label: "Total Campaign", value: totalCampaigns },
                        { label: "Total Voters", value: totalVoters.toLocaleString() },
                        { label: "Total Calls", value: "850,623" },
                        { label: "Missed Calls", value: "180" },
                    ].map((s, i) => (
                        <div key={i} className={styles.statCard}>
                            <div>{s.label}</div>
                            <strong>{s.value}</strong>
                        </div>
                    ))}
                </section>

                {/* AI Agents */}
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>AI Agents</h2>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Avatar Name</th>
                                    <th>Contact</th>
                                    <th>Gender</th>
                                    <th>Voice</th>
                                    <th>Prompt</th>
                                    <th>Language</th>
                                </tr>
                            </thead>
                            <tbody>
                                {agentRes.data?.agents?.map((a) => (
                                    <tr key={a._id}>
                                        <td>{a.avatarName}</td>
                                        <td>{a.phoneNumber}</td>
                                        <td>{a.gender}</td>
                                        <td>{a.voice?.displayName || "—"}</td>
                                        <td>{a.systemPrompt?.promptName || "—"}</td>
                                        <td>{a.language === "en-IN" ? "English" : a.language}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Bar + Balance */}
                <div className={styles.row}>
                    <div className={styles.chartSection}>
                        <div className={styles.chartHeader}>
                            <h2>Call Analysis by Prompt</h2>
                            <select className={styles.timeFilter} defaultValue="Weekly">
                                <option>Weekly</option>
                                <option>Monthly</option>
                                <option>Yearly</option>
                            </select>
                        </div>
                        <Box sx={{ height: 260 }}>
                            <BarChart
                                series={[{ data: barValues, color: "#00B8D4" }]}
                                xAxis={[{ scaleType: "band", data: barLabels }]}
                                margin={{ top: 20, right: 20, bottom: 40, left: 50 }}
                            />
                        </Box>
                    </div>

                    <div className={styles.balanceSection}>
                        <h2>Balance</h2>
                        <div className={styles.balanceCards}>
                            <div className={styles.balanceCard}>
                                <div>Current Balance</div>
                                <strong>{balanceRes.data?.data?.balance || 0}</strong>
                            </div>
                            <div className={styles.balanceCard}>
                                <div>Total Recharges</div>
                                <strong>{balanceRes.data?.data?.totalRecharges || 0}</strong>
                            </div>
                        </div>
                        <div className={styles.rechargeHistory}>
                            <h3>Recharge History</h3>
                            <div className={styles.historyItem}>
                                <span>Campaign Top-ups</span>
                                <strong className={styles.green}>500</strong>
                                <small>16th May 25</small>
                            </div>
                            <div className={styles.historyItem}>
                                <span>Initial Top-ups</span>
                                <strong className={styles.blue}>500</strong>
                                <small>15th April 25</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Call Analytics */}
                <section className={styles.section}>
                    <div className={styles.analyticsHeader}>
                        <h2>Call Analytics</h2>
                        <select className={styles.campaignSearch} defaultValue="">
                            <option value="" disabled>Search By Campaign Name</option>
                            {campaignRes.data?.data?.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.leaderName || c._id}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.analyticsGrid}>
                        {/* Pie */}
                        <div className={styles.analyticsCard}>
                            <h3>Call Status</h3>
                            <select className={styles.filterSelect} defaultValue="Weekly">
                                <option>Weekly</option>
                                <option>Monthly</option>
                                <option>Yearly</option>
                            </select>
                            <Box sx={{ height: 140 }}>
                                <PieChart
                                    series={[
                                        {
                                            data: pieData,
                                            innerRadius: 0,
                                            outerRadius: 70,
                                        },
                                    ]}
                                />
                            </Box>
                        </div>

                        {/* Sentiment */}
                        <div className={styles.analyticsCard}>
                            <h3>Voter Sentiments</h3>
                            <div className={styles.sentimentBars}>
                                {[
                                    { label: "Positive", value: 55, color: "#00C49F" },
                                    { label: "Neutral", value: 25, color: "#FFBB28" },
                                    { label: "Negative", value: 20, color: "#FF4444" },
                                ].map((s) => (
                                    <div key={s.label} className={styles.sentimentRow}>
                                        <div className={styles.sentimentLabel}>
                                            <span>{s.label}</span>   <span>{s.value}%</span>
                                        </div>
                                        <div className={styles.sentimentBarContainer}>
                                            <div
                                                className={styles.sentimentBar}
                                                style={{ width: `${s.value}%`, backgroundColor: s.color }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Line */}
                    <div className={styles.chartSection}>
                        <h3>Average Call Duration</h3>
                        <div className={styles.legend}>
                            <span className={styles.legendItem}>
                                <span className={styles.dot} style={{ background: "#1976d2" }} />
                                Oncall
                            </span>
                            <span className={styles.legendItem}>
                                <span className={styles.dot} style={{ background: "#ff9800" }} />
                                Idle
                            </span>
                        </div>
                        <select className={styles.filterSelect} defaultValue="Yearly">
                            <option>Weekly</option>
                            <option>Monthly</option>
                            <option>Yearly</option>
                        </select>
                        <Box sx={{ height: 260 }}>
                            <LineChart
                                xAxis={[{ scaleType: "point", data: months }]}
                                series={[
                                    { data: oncallVals, label: "Oncall", color: "#1976d2" },
                                ]}
                                margin={{ top: 20, right: 30, bottom: 40, left: 50 }}
                            />
                        </Box>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default ElectionDashboard;